import { useEffect, useState } from 'react';
import api from '../lib/api';

const fmt = n => Number(n || 0).toLocaleString('en-US', { style:'currency', currency:'USD', maximumFractionDigits:0 });
const fmtN = n => Number(n || 0).toLocaleString();

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('Suppliers');
  const [users, setUsers] = useState([]);
  const [filterOwner, setFilterOwner] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  const [mfrData, setMfrData] = useState([]);
  const [ownerData, setOwnerData] = useState([]);
  const [accountData, setAccountData] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data.users || [])).catch(() => {});
    loadAll();
  }, []);

  const buildParams = () => {
    const p = new URLSearchParams();
    if (filterOwner) p.append('owner_id', filterOwner);
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    return p.toString();
  };

  const loadAll = async () => {
    setLoading(true);
    const params = buildParams();
    try {
      const [mfr, owner, account, sum] = await Promise.all([
        api.get(`/reports/Suppliers?${params}`),
        api.get(`/reports/pipeline-by-owner?${params}`),
        api.get(`/reports/pipeline-by-account?${params}`),
        api.get(`/reports/summary?${params}`),
      ]);
      setMfrData(mfr.data.rows || []);
      setOwnerData(owner.data.rows || []);
      setAccountData(account.data.rows || []);
      setSummary(sum.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [filterOwner, dateFrom, dateTo]);

  const TABS = [
    { key: 'Suppliers', label: '📦 By Supplier' },
    { key: 'owner', label: '👤 By owner' },
    { key: 'account', label: '🏢 By account' },
  ];

  const totalMfrPipeline = mfrData.reduce((s, r) => s + parseFloat(r.total_pipeline || 0), 0);

  return (
    <>
      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:20, alignItems:'center', flexWrap:'wrap' }}>
        <select
          value={filterOwner}
          onChange={e => setFilterOwner(e.target.value)}
          style={{ height:32, padding:'0 10px', border:'0.5px solid var(--border-md)',
                   borderRadius:'var(--radius)', background:'var(--bg-secondary)',
                   color:'var(--text)', fontSize:13, outline:'none' }}
        >
          <option value="">All reps</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <label style={{ fontSize:12, color:'var(--text-secondary)' }}>From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ height:32, padding:'0 10px', border:'0.5px solid var(--border-md)',
                     borderRadius:'var(--radius)', background:'var(--bg-secondary)',
                     color:'var(--text)', fontSize:13, outline:'none' }} />
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <label style={{ fontSize:12, color:'var(--text-secondary)' }}>To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ height:32, padding:'0 10px', border:'0.5px solid var(--border-md)',
                     borderRadius:'var(--radius)', background:'var(--bg-secondary)',
                     color:'var(--text)', fontSize:13, outline:'none' }} />
        </div>
        {(filterOwner || dateFrom || dateTo) && (
          <button className="btn" onClick={() => { setFilterOwner(''); setDateFrom(''); setDateTo(''); }}>
            Clear filters
          </button>
        )}
        {loading && <span style={{ fontSize:12, color:'var(--text-tertiary)' }}>Loading…</span>}
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="stat-grid" style={{ marginBottom:20 }}>
          <div className="stat-card">
            <div className="stat-label">Open opportunities</div>
            <div className="stat-value">{fmtN(summary.open_opportunities)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Total pipeline</div>
            <div className="stat-value">{fmt(summary.total_pipeline)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Closed won</div>
            <div className="stat-value" style={{ color:'var(--success)' }}>{fmt(summary.closed_won_value)}</div>
            <div className="stat-change">{fmtN(summary.closed_won)} opportunities</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Closed lost</div>
            <div className="stat-value" style={{ color:'var(--danger)' }}>{fmtN(summary.closed_lost)}</div>
          </div>
        </div>
      )}

      {/* Stage breakdown bar */}
      {summary && (
        <div className="card" style={{ marginBottom:20 }}>
          <div className="section-title" style={{ marginBottom:12 }}>Pipeline by stage</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            {[
              { label:'Prospecting', key:'prospecting', color:'#F1EFE8', text:'#444441' },
              { label:'Qualified', key:'qualified', color:'#E6F1FB', text:'#0C447C' },
              { label:'Proposal', key:'proposal', color:'#FAEEDA', text:'#633806' },
              { label:'Negotiation', key:'negotiation', color:'#FBEAF0', text:'#72243E' },
            ].map(s => (
              <div key={s.key} style={{ background:s.color, borderRadius:'var(--radius)', padding:'12px 14px' }}>
                <div style={{ fontSize:11, fontWeight:600, color:s.text, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:4 }}>
                  {s.label}
                </div>
                <div style={{ fontSize:18, fontWeight:600, color:s.text }}>
                  {fmtN(summary[s.key])}
                </div>
                <div style={{ fontSize:11, color:s.text, opacity:0.7 }}>opportunities</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab navigation */}
      <div style={{ display:'flex', gap:0, marginBottom:16, borderBottom:'0.5px solid var(--border)' }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{ padding:'8px 16px', fontSize:13, border:'none', cursor:'pointer',
                     background:'transparent',
                     borderBottom: activeTab === tab.key ? '2px solid var(--accent)' : '2px solid transparent',
                     color: activeTab === tab.key ? 'var(--accent)' : 'var(--text-secondary)',
                     fontWeight: activeTab === tab.key ? 500 : 400 }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Supplier REPORT */}
      {activeTab === 'Suppliers' && (
        <div className="card" style={{ padding:0 }}>
          <div style={{ padding:'14px 16px', borderBottom:'0.5px solid var(--border)',
                        display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div className="section-title" style={{ marginBottom:0 }}>
              Pipeline by Supplier
            </div>
            <div style={{ fontSize:12, color:'var(--text-secondary)' }}>
              Total: {fmt(totalMfrPipeline)}
            </div>
          </div>
          {mfrData.length === 0
            ? <div style={{ padding:24, fontSize:13, color:'var(--text-tertiary)', textAlign:'center' }}>
                No line items found. Add products to opportunities to see Supplier reports.
              </div>
            : <table className="data-table">
                <thead>
                  <tr>
                    <th>Supplier</th>
                    <th>Opportunities</th>
                    <th>Prospecting</th>
                    <th>Qualified</th>
                    <th>Proposal</th>
                    <th>Negotiation</th>
                    <th>Closed won</th>
                    <th>Total pipeline</th>
                    <th>% of total</th>
                  </tr>
                </thead>
                <tbody>
                  {mfrData.map((r, i) => {
                    const pct = totalMfrPipeline > 0
                      ? ((parseFloat(r.total_pipeline) / totalMfrPipeline) * 100).toFixed(1)
                      : 0;
                    return (
                      <tr key={i}>
                        <td style={{ fontWeight:500 }}>{r.Supplier}</td>
                        <td style={{ color:'var(--text-secondary)' }}>{fmtN(r.opportunity_count)}</td>
                        <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{fmt(r.prospecting)}</td>
                        <td style={{ fontSize:12, color:'#0C447C' }}>{fmt(r.qualified)}</td>
                        <td style={{ fontSize:12, color:'#633806' }}>{fmt(r.proposal)}</td>
                        <td style={{ fontSize:12, color:'#72243E' }}>{fmt(r.negotiation)}</td>
                        <td style={{ fontSize:12, color:'var(--success)', fontWeight:500 }}>{fmt(r.closed_won)}</td>
                        <td style={{ fontWeight:600, color:'var(--accent-dark)' }}>{fmt(r.total_pipeline)}</td>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div style={{ flex:1, height:6, background:'var(--bg-tertiary)', borderRadius:3, overflow:'hidden' }}>
                              <div style={{ width:`${pct}%`, height:'100%', background:'var(--accent)', borderRadius:3 }} />
                            </div>
                            <span style={{ fontSize:11, color:'var(--text-secondary)', minWidth:32 }}>{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ fontWeight:600, padding:'10px 12px' }}>Total</td>
                    <td style={{ padding:'10px 12px', color:'var(--text-secondary)' }}>
                      {fmtN(mfrData.reduce((s,r) => s + parseInt(r.opportunity_count||0), 0))}
                    </td>
                    <td colSpan={5}></td>
                    <td style={{ fontWeight:700, color:'var(--accent-dark)', padding:'10px 12px', fontSize:15 }}>
                      {fmt(totalMfrPipeline)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
          }
        </div>
      )}

      {/* PIPELINE BY OWNER */}
      {activeTab === 'owner' && (
        <div className="card" style={{ padding:0 }}>
          <div style={{ padding:'14px 16px', borderBottom:'0.5px solid var(--border)' }}>
            <div className="section-title" style={{ marginBottom:0 }}>Pipeline by owner</div>
          </div>
          {ownerData.length === 0
            ? <div style={{ padding:24, fontSize:13, color:'var(--text-tertiary)', textAlign:'center' }}>
                No data found.
              </div>
            : <table className="data-table">
                <thead>
                  <tr>
                    <th>Rep</th>
                    <th>Open opportunities</th>
                    <th>Pipeline value</th>
                    <th>Closed won (count)</th>
                    <th>Closed won (value)</th>
                  </tr>
                </thead>
                <tbody>
                  {ownerData.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight:500 }}>{r.owner_name}</td>
                      <td><span className="badge badge-blue">{fmtN(r.open_opportunities)}</span></td>
                      <td style={{ fontWeight:600, color:'var(--accent-dark)' }}>{fmt(r.pipeline_value)}</td>
                      <td><span className="badge badge-green">{fmtN(r.closed_won_count)}</span></td>
                      <td style={{ fontWeight:500, color:'var(--success)' }}>{fmt(r.closed_won_value)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ fontWeight:600, padding:'10px 12px' }}>Total</td>
                    <td style={{ padding:'10px 12px' }}>
                      <span className="badge badge-blue">
                        {fmtN(ownerData.reduce((s,r) => s + parseInt(r.open_opportunities||0), 0))}
                      </span>
                    </td>
                    <td style={{ fontWeight:700, color:'var(--accent-dark)', padding:'10px 12px', fontSize:15 }}>
                      {fmt(ownerData.reduce((s,r) => s + parseFloat(r.pipeline_value||0), 0))}
                    </td>
                    <td style={{ padding:'10px 12px' }}>
                      <span className="badge badge-green">
                        {fmtN(ownerData.reduce((s,r) => s + parseInt(r.closed_won_count||0), 0))}
                      </span>
                    </td>
                    <td style={{ fontWeight:500, color:'var(--success)', padding:'10px 12px' }}>
                      {fmt(ownerData.reduce((s,r) => s + parseFloat(r.closed_won_value||0), 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
          }
        </div>
      )}

      {/* PIPELINE BY ACCOUNT */}
      {activeTab === 'account' && (
        <div className="card" style={{ padding:0 }}>
          <div style={{ padding:'14px 16px', borderBottom:'0.5px solid var(--border)' }}>
            <div className="section-title" style={{ marginBottom:0 }}>Top 25 accounts by pipeline</div>
          </div>
          {accountData.length === 0
            ? <div style={{ padding:24, fontSize:13, color:'var(--text-tertiary)', textAlign:'center' }}>
                No data found.
              </div>
            : <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Account</th>
                    <th>Open opportunities</th>
                    <th>Pipeline value</th>
                    <th>Closed won</th>
                  </tr>
                </thead>
                <tbody>
                  {accountData.map((r, i) => (
                    <tr key={i}>
                      <td style={{ color:'var(--text-tertiary)', fontSize:12 }}>{i + 1}</td>
                      <td style={{ fontWeight:500 }}>{r.account_name}</td>
                      <td><span className="badge badge-blue">{fmtN(r.open_opportunities)}</span></td>
                      <td style={{ fontWeight:600, color:'var(--accent-dark)' }}>{fmt(r.pipeline_value)}</td>
                      <td style={{ fontWeight:500, color:'var(--success)' }}>{fmt(r.closed_won_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          }
        </div>
      )}
    </>
  );
}